import { supabase } from './supabase';
import { notificationService } from './services.notification';

export interface LabTest {
  id: string;
  code: string;
  name: string;
  department: string;
  sample_type: string;
  tube?: string;
  tat_hours: number;
  price: number;
  is_panel: boolean;
  active: boolean;
  method?: string;
  machine_model?: string;
  default_priority?: 'routine' | 'urgent' | 'stat';
  qc_required?: boolean;
  created_at: string;
  updated_at: string;
}

export interface LabPanelItem {
  id: string;
  panel_id: string;
  test_id: string;
  display_order: number;
  test?: LabTest;
}

export interface LabOrder {
  id: string;
  patient_id: string;
  ordered_by?: string;
  external_doctor?: string;
  source: 'internal' | 'external' | 'self';
  status: 'ordered' | 'collected' | 'in_progress' | 'verified' | 'finalized' | 'cancelled';
  invoice_id?: string;
  payment_status?: 'pending' | 'partial' | 'paid';
  tenant_id?: string;
  notes?: string;
  priority?: 'routine' | 'urgent' | 'stat';
  due_at?: string;
  completed_at?: string;
  tat_minutes?: number;
  created_at: string;
  updated_at: string;
  patient?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    date_of_birth?: string;
  };
  invoice?: {
    id: string;
    total: number;
    paid_amount: number;
  } | null;
}

export interface LabOrderItem {
  id: string;
  order_id: string;
  test_id: string;
  status: 'ordered' | 'in_progress' | 'verified' | 'finalized' | 'cancelled';
  sample_id?: string;
  price: number;
  discount: number;
  created_at: string;
  updated_at: string;
  test?: LabTest;
}

export interface LabSample {
  id: string;
  order_id: string;
  patient_id: string;
  sample_type: string;
  collected_at?: string;
  collected_by?: string;
  barcode?: string;
  notes?: string;
  created_at: string;
}

export interface LabResult {
  id: string;
  order_item_id: string;
  analyte: string;
  value?: string;
  unit?: string;
  ref_low?: number;
  ref_high?: number;
  flag?: string;
  entered_by?: string;
  verified_by?: string;
  verified_at?: string;
  remarks?: string;
  created_at: string;
  updated_at: string;
}

export interface LabReport {
  id: string;
  order_id: string;
  report_no: string;
  pdf_url?: string;
  finalized_at: string;
  finalized_by?: string;
  created_at: string;
}

export const labTestService = {
  async getAll(activeOnly = true): Promise<LabTest[]> {
    let query = supabase.from('lab_tests').select('*').order('name');
    
    if (activeOnly) {
      query = query.eq('active', true);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<LabTest | null> {
    const { data, error } = await supabase
      .from('lab_tests')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async getPanelItems(panelId: string): Promise<LabPanelItem[]> {
    const { data, error } = await supabase
      .from('lab_panel_items')
      .select('*, test:test_id(*)')
      .eq('panel_id', panelId)
      .order('display_order');
    
    if (error) throw error;
    return data || [];
  },

  async create(test: Omit<LabTest, 'id' | 'created_at' | 'updated_at'>): Promise<LabTest> {
    const { data, error } = await supabase
      .from('lab_tests')
      .insert(test)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, test: Partial<LabTest>): Promise<LabTest> {
    const { data, error } = await supabase
      .from('lab_tests')
      .update({ ...test, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

export const labOrderService = {
  async getAll(filters?: { status?: string; patientId?: string }): Promise<LabOrder[]> {
    let query = supabase
      .from('lab_orders')
      .select(`
        *,
        patient:patient_id(id, name, email, phone),
        invoice:invoice_id(id, total)
      `)
      .order('created_at', { ascending: false });
    
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    
    if (filters?.patientId) {
      query = query.eq('patient_id', filters.patientId);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    if (data && data.length > 0) {
      const invoiceIds = data
        .filter(order => order.invoice_id)
        .map(order => order.invoice_id);
      
      if (invoiceIds.length > 0) {
        const { data: payments } = await supabase
          .from('payments')
          .select('invoice_id, amount')
          .in('invoice_id', invoiceIds);
        
        const paidAmounts: Record<string, number> = {};
        if (payments) {
          payments.forEach(payment => {
            paidAmounts[payment.invoice_id] = (paidAmounts[payment.invoice_id] || 0) + payment.amount;
          });
        }
        
        data.forEach(order => {
          if (order.invoice && order.invoice_id) {
            (order.invoice as any).paid_amount = paidAmounts[order.invoice_id] || 0;
          }
        });
      }
    }
    
    return data || [];
  },

  async getById(id: string): Promise<LabOrder | null> {
    const { data, error } = await supabase
      .from('lab_orders')
      .select('*, patient:patient_id(id, name, email, phone, date_of_birth)')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(order: Omit<LabOrder, 'id' | 'created_at' | 'updated_at'>): Promise<LabOrder> {
    const { data, error } = await supabase
      .from('lab_orders')
      .insert(order)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: LabOrder['status']): Promise<LabOrder> {
    const { data, error } = await supabase
      .from('lab_orders')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

export const labOrderItemService = {
  async getByOrder(orderId: string): Promise<LabOrderItem[]> {
    const { data, error } = await supabase
      .from('lab_order_items')
      .select('*, test:test_id(*)')
      .eq('order_id', orderId);
    
    if (error) throw error;
    return data || [];
  },

  async create(item: Omit<LabOrderItem, 'id' | 'created_at' | 'updated_at'>): Promise<LabOrderItem> {
    const { data, error } = await supabase
      .from('lab_order_items')
      .insert(item)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: LabOrderItem['status']): Promise<LabOrderItem> {
    const { data, error } = await supabase
      .from('lab_order_items')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

export const labSampleService = {
  async getByOrder(orderId: string): Promise<LabSample[]> {
    const { data, error } = await supabase
      .from('lab_samples')
      .select('*')
      .eq('order_id', orderId);
    
    if (error) throw error;
    return data || [];
  },

  async getByBarcode(barcode: string): Promise<LabSample | null> {
    const { data, error } = await supabase
      .from('lab_samples')
      .select('*')
      .eq('barcode', barcode)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(sample: Omit<LabSample, 'id' | 'created_at'>): Promise<LabSample> {
    const { data, error } = await supabase
      .from('lab_samples')
      .insert(sample)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async generateBarcode(orderId: string): Promise<string> {
    return `LAB-${Date.now()}-${orderId.substring(0, 8).toUpperCase()}`;
  }
};

export const labResultService = {
  async getByOrderItem(orderItemId: string): Promise<LabResult[]> {
    const { data, error } = await supabase
      .from('lab_results')
      .select('*')
      .eq('order_item_id', orderItemId)
      .order('analyte');
    
    if (error) throw error;
    return data || [];
  },

  async create(result: Omit<LabResult, 'id' | 'created_at' | 'updated_at'>): Promise<LabResult> {
    const { data, error } = await supabase
      .from('lab_results')
      .insert(result)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, result: Partial<LabResult>): Promise<LabResult> {
    const { data, error } = await supabase
      .from('lab_results')
      .update({ ...result, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async verify(id: string, userId: string): Promise<LabResult> {
    const { data, error } = await supabase
      .from('lab_results')
      .update({
        verified_by: userId,
        verified_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

export const labReportService = {
  async getByOrder(orderId: string): Promise<LabReport | null> {
    const { data, error } = await supabase
      .from('lab_reports')
      .select('*')
      .eq('order_id', orderId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(report: Omit<LabReport, 'id' | 'created_at'>): Promise<LabReport> {
    const { data, error } = await supabase
      .from('lab_reports')
      .insert(report)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async generateReportNumber(): Promise<string> {
    return `RPT-${Date.now()}`;
  },

  async uploadPDF(orderId: string, pdfBlob: Blob): Promise<string> {
    const fileName = `report-${orderId}-${Date.now()}.pdf`;
    const { data, error } = await supabase.storage
      .from('lab-reports')
      .upload(fileName, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      });
    
    if (error) throw error;
    return fileName;
  },

  async getSignedUrl(fileName: string, expiresIn = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from('lab-reports')
      .createSignedUrl(fileName, expiresIn);
    
    if (error) throw error;
    return data.signedUrl;
  }
};

export async function notifyReportReady(
  patientId: string, 
  patientName: string,
  orderId: string,
  orderNo: string
) {
  try {
    await notificationService.notifyResultReady(patientId, patientName, 'lab', orderNo);
  } catch (error) {
    console.error('Failed to send notification:', error);
  }
}

export async function generateLabReportPDFBlob(
  reportData: {
    reportNo: string;
    patient?: { name: string; phone?: string };
    orderId: string;
    orderItems: LabOrderItem[];
    results: Record<string, LabResult[]>;
    finalizedBy: string;
    finalizedAt: Date;
  }
): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('Laboratory Report', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Report No: ${reportData.reportNo}`, 20, 40);
  doc.text(`Patient: ${reportData.patient?.name || 'Unknown'}`, 20, 50);
  doc.text(`Phone: ${reportData.patient?.phone || 'N/A'}`, 20, 60);
  doc.text(`Date: ${reportData.finalizedAt.toLocaleDateString()}`, 20, 70);
  
  let yPos = 90;
  doc.setFontSize(14);
  doc.text('Test Results', 20, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  for (const item of reportData.orderItems) {
    const itemResults = reportData.results[item.id] || [];
    
    doc.setFontSize(12);
    doc.text(item.test?.name || 'Unknown Test', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    for (const result of itemResults) {
      const line = `${result.analyte}: ${result.value} ${result.unit || ''} (Ref: ${result.ref_low}-${result.ref_high})`;
      doc.text(line, 25, yPos);
      
      if (result.flag) {
        const flagText = result.flag === 'H' ? '[HIGH]' : result.flag === 'L' ? '[LOW]' : '[NORMAL]';
        doc.text(flagText, 180, yPos);
      }
      
      yPos += 6;
      
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
    }
    
    yPos += 5;
  }
  
  const qrData = `${window.location.origin}/verify/lab/${reportData.orderId}?token=${reportData.reportNo}`;
  doc.text('Scan to verify:', 20, yPos + 10);
  doc.text(qrData, 20, yPos + 20, { maxWidth: 170 });
  
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text('This is a computer-generated report and does not require a physical signature.', pageWidth / 2, pageHeight - 20, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
  
  const order = reportData as any;
  if (order.priority || order.tat_minutes) {
    doc.setFontSize(8);
    const footerY = pageHeight - 10;
    if (order.priority) {
      doc.text(`Priority: ${order.priority.toUpperCase()}`, 15, footerY);
    }
    if (order.tat_minutes) {
      const tatHours = Math.floor(order.tat_minutes / 60);
      const tatMins = Math.floor(order.tat_minutes % 60);
      doc.text(`TAT: ${tatHours}h ${tatMins}m`, pageWidth - 15, footerY, { align: 'right' });
    }
  }
  
  return doc.output('blob');
}
